import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { DashboardLayout } from '@src/shared/ui/layouts/Dashboard';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { isEmpty, isUndefined } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { Details } from './components/Details';
import { Form } from './components/Form';
import { useSearch } from './hooks/useSearch';
import { widgets } from './includes/configuration';

/**
 * Page: RiskDashboard
 *
 * Provides the user with a screen to view various statistics relating to risk records.
 *
 * @returns {Element}
 * @constructor
 */
export function RiskDashboard() {
  const { currentRoute } = useRoutesContext();

  const [reportDate, setReportDate] = useState(dayjs().format('MM/DD/YYYY'));

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListRiskCodes',
    },
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListRedRiskCodes',
    },
    {
      apiKey: 'Event',
      queryKey: 'ListEventTypes',
    },
    {
      apiKey: 'Source',
      queryKey: 'ListImpactedSystems',
    },
  ]);
  const { data, handleSearch, isSearching } = useSearch();

  useEffect(() => {
    const theDate = dayjs(reportDate).format('YYYY-MM-DD');

    if (
      (!isEmpty(reportDate) && isEmpty(data)) ||
      (!isSearching && data?.GetRiskDashboardStatistics?.data?.date !== theDate)
    ) {
      if (!isUndefined(assets?.ListEventTypes?.data)) {
        const eventTypeCodeList =
          assets?.ListEventTypes?.data?.map((eventType) => {
            return eventType?.codeId;
          }) || [];

        handleSearch({ eventTypeCodeList, reportDate });
      }
    }
  }, [assets, isSearching, reportDate, data]);

  const theWidgets = useMemo(() => {
    return Object.values(widgets)
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((widget) => {
        const theWidget = { ...widget };

        if (widget.accessor === 'events') {
          theWidget.description = `${dayjs(reportDate).subtract(6, 'months').format('MM/DD/YYYY')} - ${dayjs(
            reportDate
          ).format('MM/DD/YYYY')}`;
        }

        if (widget.accessor === 'dataContainment') {
          theWidget.description = `${dayjs(data?.ListTopContainmentSources?.data?.startDate).format(
            'MM/DD/YYYY'
          )} - ${dayjs(data?.ListTopContainmentSources?.data?.endDate).format('MM/DD/YYYY')}`;
        }

        theWidget.icon = <IconSymbol color="#000000" icon={widget?.icon} variant="outlined" />;

        const Component = widgets?.[theWidget?.accessor]?.component;

        const componentProps = {
          assets,
          data,
          options: theWidget,
        };

        if (widget?.accessor === 'dataCollision') {
          componentProps.statusCodeCounts = data?.['GetActionPathInitiatives-DataCollision']?.data?.statusCodeCounts;
        }

        theWidget.element = Component ? <Component key={widget.accessor} {...componentProps} /> : null;

        return theWidget;
      });
  }, [widgets, assets, data]);

  return (
    <ErrorHandler location="src/pages/private/Dashboards/RiskDashboard/RiskDashboard.jsx">
      <DashboardLayout
        hasData={!isUndefined(data)}
        header={{
          LeftComponent: (
            <Form
              isFetching={isSearching}
              isLoading={isLoadingAssets}
              reportDate={reportDate}
              setReportDate={setReportDate}
            />
          ),
          RightComponent: <Details data={data} />,
        }}
        isFetching={isSearching}
        isLoading={isLoadingAssets}
        title={currentRoute?.screenTitle}
        widgets={theWidgets}
      />
    </ErrorHandler>
  );
}
